import { Marker } from '../../components/ItemsMap/types';
import { MapBounds, ResidenceMapMarker } from '../../graphql/graphql';

export const calculateMapBounds = (
  markers?: ResidenceMapMarker[]
): MapBounds | null => {
  if (!markers?.length) return null;

  const lats = markers.map(m => m.lat);
  const lngs = markers.map(m => m.lng);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  return {
    topLeft: {
      latitude: maxLat,
      longitude: minLng
    },
    bottomRight: {
      latitude: minLat,
      longitude: maxLng
    }
  };
};

/**
 * Base tile size used by Google Maps.
 */
const TILE_SIZE = 256;

/**
 * Cluster radius in screen pixels.
 */
const CLUSTER_RADIUS = 60;

/**
 * Projects geographic coordinates (lat/lng)
 * into pixel coordinates for a specific zoom level.
 */
export const projectToPixel = (lat: number, lng: number, zoom: number) => {
  const siny = Math.sin((lat * Math.PI) / 180);
  const clampedSiny = Math.min(Math.max(siny, -0.9999), 0.9999);
  const scale = TILE_SIZE * 2 ** zoom;

  const x = ((lng + 180) / 360) * scale;
  const y =
    (0.5 - Math.log((1 + clampedSiny) / (1 - clampedSiny)) / (4 * Math.PI)) *
    scale;

  return { x, y };
};

/**
 * Builds map markers with custom clustering logic.
 *
 * Clustering strategy:
 * - Convert each marker into pixel space
 * - Group markers within CLUSTER_RADIUS
 * - Return either individual marker or cluster marker
 */
export const buildMarkers = (
  residences?: ResidenceMapMarker[],
  zoom?: number
): Marker[] => {
  if (!residences?.length || zoom === undefined) {
    return [];
  }

  const clusters: {
    x: number;
    y: number;
    points: ResidenceMapMarker[];
  }[] = [];

  const radiusSq = CLUSTER_RADIUS * CLUSTER_RADIUS;

  residences.forEach(residence => {
    const { x, y } = projectToPixel(residence.lat, residence.lng, zoom);

    const cluster = clusters.find(c => {
      const dx = c.x - x;
      const dy = c.y - y;
      return dx * dx + dy * dy < radiusSq;
    });

    if (cluster) {
      cluster.points.push(residence);
    } else {
      clusters.push({
        x,
        y,
        points: [residence]
      });
    }
  });

  return clusters.map(cluster => {
    const { points } = cluster;

    if (points.length === 1) {
      const item = points[0];

      return {
        id: item.residenceId,
        type: 'ItemMapMarker',
        position: { lat: item.lat, lng: item.lng },
        label: item.name
      };
    }

    const { sumLat, sumLng } = points.reduce(
      (acc, p) => ({
        sumLat: acc.sumLat + p.lat,
        sumLng: acc.sumLng + p.lng
      }),
      { sumLat: 0, sumLng: 0 }
    );

    const avgLat = sumLat / points.length;
    const avgLng = sumLng / points.length;

    return {
      id: `cluster_${avgLat}_${avgLng}`,
      type: 'ClusterMapMarker',
      position: { lat: avgLat, lng: avgLng },
      label: points.length,
      itemsIds: points.map(p => p.residenceId)
    };
  });
};
